<template lang="pug">
  div(ref="select" :class="[wrapClass, {'opened': show}]")
    .autocomplete-input-group(v-if="!dropdownMode" :class="{'autocomplete-selected': value}")
      input( type="search" v-model="searchText" v-bind="inputAttrs",
      ref="autocomplete",
      autocomplete="off",
      :class="inputAttrs.class || inputClass",
      :placeholder="inputAttrs.placeholder || placeholder",
      :disabled="inputAttrs.disabled || disabled",
      @blur="blur", @focus="focus", @input="inputChange",
      @keyup.enter="keyEnter", @keydown.tab="keyEnter",
      @keydown.up="keyUp", @keydown.down="keyDown")
      slot(name="input-icon")
    .autocomplete-list(v-if="show" @scroll="checkItemsThreshold")
      .autocomplete-list-item.no-matches(v-if="!hasItems")
        .autocomplete-item {{noMatchesText}}
      .autocomplete-list-group(v-else-if="groupOptions" v-for="group, groupIndex in internalItems", :key="groupIndex")
        .autocomplete-list-group-title(v-if="group.title") {{group.title}}
        .autocomplete-list-item(v-for="item, i in group.items", :key="i", @click="onClickItem(item)",
        :class="{'autocomplete-item-active': getCursor(i, groupIndex) === cursor}",
        @mouseover="cursor = getCursor(i, groupIndex)")
          slot(:item="item")
            .autocomplete-item {{getLabel(item)}}
      .autocomplete-list-item(v-else, v-for="item, i in internalItemsLimited", :key="i", @click="onClickItem(item)",
      :class="{'autocomplete-item-active': i === cursor}", @mouseover="cursor = i")
        slot(:item="item")
          .autocomplete-item {{getLabel(item)}}
</template>

<script>
import _ from 'lodash'
import utils from '../../../utils/autocomplete'

const ITEMS_THRESHOLD_STEP = 50

export default {
  props: {
    dropdownMode: {
      type: Boolean,
      default: false,
    },
    minLen: {
      type: Number,
      default: utils.minLen,
    },
    wait: {
      type: Number,
      default: utils.wait,
    },
    value: {
      type: String,
      default: '',
    },
    getLabel: {
      type: Function,
      default: (item) => item,
    },
    getValue: {
      type: [ Function, Boolean, ],
      default: false,
    },
    rootClass: {
      type: String,
      default: null,
    },
    groupOptions: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      required: true,
    },
    focusOnCreate: {
      type: Boolean,
      default: false,
    },
    noMatchesText: {
      type: String,
      default: 'No matched items...',
    },
    autoSelectOneItem: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    inputClass: {
      type: String,
      default: 'autocomplete-input',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    inputAttrs: {
      type: Object,
      default: () => {
        return {}
      },
    },
    keepOpen: {
      type: Boolean,
      default: true,
    },
  },
  data () {
    return {
      searchText: '',
      showList: false,
      cursor: -1,
      internalItems: this.items || [],
      itemsThreshold: ITEMS_THRESHOLD_STEP,
    }
  },
  computed: {
    mappedItems () {
      return this.groupOptions ? this.internalItems.reduce((acc, { items, }) => {
        return [ ...acc, ...items, ]
      }, []) : this.internalItems
    },
    hasItems () {
      return !!this.internalItems.length
    },
    wrapClass () {
      return this.rootClass ? this.rootClass : 'autocomplete'
    },
    show () {
      return this.showList || this.keepOpen
    },
    internalItemsAmount () {
      return this.internalItems.length
    },
    internalItemsLimited () {
      if (this.itemsThreshold < this.internalItemsAmount) {
        return this.internalItems.slice(0, this.itemsThreshold)
      } else {
        return this.internalItems
      }
    },
  },
  watch: {
    items (newValue) {
      this.setItems(newValue)
      const item = utils.findItem(this.items, this.searchText, this.autoSelectOneItem)
      if (item) {
        this.onSelectItem(item)
        this.showList = false
      }
    },
    searchText (newValue) {
      if (!newValue) this.setItems(this.items)
    },
    value (newValue) {
      if (!this.isSelectedValue(newValue)) {
        // this.onSelectItem(newValue)
        this.searchText = this.getLabel(newValue)
        this.$emit('change', this.searchText)
      }
    },
  },
  created () {
    utils.minLen = this.minLen
    utils.wait = this.wait
    this.onSelectItem(this.value, true)
  },
  mounted () {
    if (this.focusOnCreate && !this.dropdownMode) this.focus()
    this.scrollWindowToFullyShowDropdownIfNeeded()
  },
  methods: {
    scrollWindowToFullyShowDropdownIfNeeded () {
      let findModal = (el) => {
        let parentNode = el.parentElement
        if (!parentNode) {
          return undefined
        }
        if (window.getComputedStyle(parentNode).position === 'fixed') {
          return el
        }
        return findModal(parentNode)
      }

      let modal = findModal(this.$refs.select)
      let viewportHeight = window.innerHeight || document.documentElement.clientHeight
      let dropdownRect = this.$refs.select.getBoundingClientRect()
      if (viewportHeight && viewportHeight > dropdownRect.height) {
        if (viewportHeight < dropdownRect.height + dropdownRect.top) {
          if (modal) {
            let top = Math.max(0, modal.scrollTop + dropdownRect.top - 50)
            modal.scrollTo({
              top,
              behavior: 'smooth',
            })
          } else {
            let top = Math.max(0, window.scrollY + dropdownRect.top - 50)
            window.scrollTo({
              top: top,
              behavior: 'smooth',
            })
          }
        }
      }
    },
    checkItemsThreshold (event){
      if (this.itemsThreshold < this.internalItemsAmount) {
        if (_.hasIn(event, 'target.scrollHeight') && _.hasIn(event, 'target.scrollTop') && _.hasIn(event, 'target.offsetHeight')) {
          let pxLeftToBottomBorder
          if (_.hasIn(event, 'target.scrollTopMax')) {
            pxLeftToBottomBorder = event.target.scrollTopMax - event.target.scrollTop
          } else {
            let scrollTopMax = event.target.scrollHeight - event.target.offsetHeight
            pxLeftToBottomBorder = scrollTopMax - event.target.scrollTop
          }
          if (pxLeftToBottomBorder < 100) {
            this.itemsThreshold += ITEMS_THRESHOLD_STEP
          }
        } else {
          this.itemsThreshold = this.internalItemsAmount
        }
      }
    },
    getCursor (index, groupIndex) {
      return index + (groupIndex < 1 ? 0 : this.internalItems[groupIndex - 1].items.length)
    },
    inputChange () {
      this.itemsThreshold = ITEMS_THRESHOLD_STEP
      this.showList = true
      this.cursor = -1
      // this.onSelectItem(null, 'inputChange')
      utils.callUpdateItems(this.searchText, this.updateItems)
      this.$emit('change', this.searchText)
    },
    updateItems () {
      this.$emit('update-items', this.searchText)
    },
    focus () {
      this.$refs.autocomplete.focus()
      this.$emit('focus', this.searchText)
      this.showList = true
    },
    blur () {
      this.$emit('blur', this.searchText)
      setTimeout(() => {
        this.showList = false
      }, 150)
    },
    onClickItem (item) {
      this.onSelectItem(item)
      this.$emit('item-clicked', item)
    },
    onSelectItem (item, isCreated) {
      if (item) {
        // this.internalItems = [item]
        this.searchText = this.getValue ? this.getValue(item) : this.getLabel(item)
        if (!isCreated) {
          this.$emit('item-selected', item)
          this.clearAutocompleteText()
        }
      } else {
        this.setItems(this.items)
      }
      this.$emit('input', item)
    },
    setItems (items) {
      this.internalItems = items || []
    },
    isSelectedValue (value) {
      return this.internalItems.length === 1 && value === this.internalItems[0]
    },
    keyUp (e) {
      if (this.cursor > -1) {
        this.cursor--
        this.itemView(this.$el.getElementsByClassName('autocomplete-list-item')[this.cursor])
      }
    },
    keyDown (e) {
      if (this.cursor < this.mappedItems.length) {
        this.cursor++
        this.itemView(this.$el.getElementsByClassName('autocomplete-list-item')[this.cursor])
      }
    },
    itemView (item) {
      if (item) item.parentNode.scrollTop = item.offsetTop - 60
    },
    keyEnter (e) {
      if (this.showList && this.mappedItems[this.cursor]) {
        this.onSelectItem(this.mappedItems[this.cursor])
        this.showList = false
      } else {
        this.$emit('enter', e.target.value)
        this.showList = false
      }
    },
    clearAutocompleteText () {
      this.searchText = ''
      if (this.$refs.autocomplete) this.focus()
    },
  },
}
</script>

<style lang="stylus">
  .autocomplete-list .autocomplete-list-item.no-matches
    cursor auto

    &:hover
      background-color none
</style>



// WEBPACK FOOTER //
// src/components/lei-components/inputs/Autocomplete.vue